import '../../i18n/autoTranslate';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

import { selectSessions, selectSessionLoading } from '../../../features/upcoming_sessions/upcomingSessionSlice';
import { selectChallenges, selectChallengeLoading } from '../../../features/challenges/challengeSlice';
import { selectTips, selectTipLoading } from '../../../features/ai_tips/aiTipSlice';
import { fetchUpcomingSessions } from '../../../features/upcoming_sessions/upcomingSessionThunk';
import { fetchChallenges } from '../../../features/challenges/challengeThunk';
import { fetchAITips } from '../../../features/ai_tips/aiTipThunk';

// [NEW] Unified sidebar skeleton to replace 3 separate spinners.
const SidebarSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 shadow-[0_14px_50px_rgba(0,0,0,0.30)]">
      <div className="h-5 w-1/3 rounded bg-white/10" />
      <div className="mt-3 h-3 w-2/3 rounded bg-white/10" />
      <div className="mt-5 space-y-3">
        <div className="h-16 rounded-xl bg-white/10" />
        <div className="h-16 rounded-xl bg-white/10" />
      </div>
    </div>

    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 shadow-[0_14px_50px_rgba(0,0,0,0.30)]">
      <div className="h-5 w-1/3 rounded bg-white/10" />
      <div className="mt-5 h-24 rounded-xl bg-white/10" />
    </div>

    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 shadow-[0_14px_50px_rgba(0,0,0,0.30)]">
      <div className="h-5 w-1/3 rounded bg-white/10" />
      <div className="mt-5 space-y-3">
        <div className="h-14 rounded-xl bg-white/10" />
        <div className="h-14 rounded-xl bg-white/10" />
      </div>
    </div>
  </div>
);

const UpcomingSessions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const upcomingSessions = useSelector(selectSessions);
  const sessionsLoading = useSelector(selectSessionLoading);

  const dailyChallenges = useSelector(selectChallenges);
  const challengesLoading = useSelector(selectChallengeLoading);

  const aiTips = useSelector(selectTips);
  const tipsLoading = useSelector(selectTipLoading);

  useEffect(() => {
    dispatch(fetchUpcomingSessions({ status: 'scheduled', limit: 5 }));
    dispatch(fetchChallenges({ is_daily: true }));
    dispatch(fetchAITips({ limit: 4, random: true }));
  }, [dispatch]);

  const initialLoading = sessionsLoading || challengesLoading || tipsLoading;
  const hasAnyData = Boolean((upcomingSessions?.length || 0) + (dailyChallenges?.length || 0) + (aiTips?.length || 0));

  const formatScheduledTime = (time) => {
    const scheduledTime = new Date(time);
    const now = new Date();
    const diff = scheduledTime - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    return 'starting soon';
  };

  const getDifficultyColor = (difficulty) => {
    switch (String(difficulty || '').toLowerCase()) {
      case 'easy':
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'medium':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
      case 'hard':
        return 'text-red-400 bg-red-400/10 border-red-400/20';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const scheduleCta = useMemo(
    () => (
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => navigate('/upcoming-sessions-planner')}
      >
        + Schedule New Session
      </Button>
    ),
    [navigate]
  );

  // [NEW] Unified skeleton on first load only.
  if (initialLoading && !hasAnyData) return <SidebarSkeleton />;

  return (
    <div className="space-y-6">
      {/* Upcoming Sessions */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Upcoming Sessions</h3>

        {upcomingSessions?.length > 0 ? (
          <div className="space-y-3">
            {upcomingSessions.map((session) => (
              <SpotlightCard
                key={session?.id}
                className="custom-spotlight-card relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
                spotlightColor={session?.spotlight}
              >
                <div className="relative z-10 p-4 hover:bg-white/5 transition-all duration-200 ease-smooth rounded-2xl">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400">
                      <Icon name={session?.icon || 'Calendar'} size={18} />
                    </div>

                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="text-sm font-medium text-white truncate">{session?.title}</h4>
                          <p className="text-xs text-gray-300 truncate">{session?.company} • {session?.type}</p>
                        </div>
                        {session?.difficulty && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getDifficultyColor(session?.difficulty)}`}>
                            {session?.difficulty}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Icon name="Clock" size={12} />
                            <span>{session?.duration} min</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={12} />
                            <span>{formatScheduledTime(session?.scheduled_time)}</span>
                          </span>
                        </div>

                        {/* FIX: Join button needs onClick handler */}
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => {
                            // TODO: update this route to match your interview room path
                            navigate(`/ai-interview/${session?.id}`);
                          }}
                        >
                          Join
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            ))}

            {/* FIX: show schedule CTA even when sessions exist */}
            {scheduleCta}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <div className="w-full flex justify-center">
              <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl flex items-center justify-center">
                <Icon name="Calendar" size={32} className="text-white/55" />
              </div>
            </div>
            <p className="mt-3 text-sm">No upcoming sessions</p>
            <div className="mt-4">{scheduleCta}</div>
          </div>
        )}
      </div>

      {/* Daily Challenges */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Daily Challenges</h3>

        {dailyChallenges?.length > 0 ? (
          <div className="space-y-3">
            {dailyChallenges.map((challenge) => (
              <SpotlightCard
                key={challenge?.id}
                className="custom-spotlight-card relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
                spotlightColor={challenge?.spotlight}
              >
                <div className="relative z-10 p-4 rounded-2xl hover:bg-white/5 transition-all duration-200 ease-smooth">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400">
                      <Icon name={challenge?.icon || 'Target'} size={16} />
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-medium text-white">{challenge?.title}</h4>
                          <p className="text-xs text-gray-300">{challenge?.description}</p>
                        </div>
                        <span className="text-xs text-purple-400 font-medium">+{challenge?.xp_reward} XP</span>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Progress</span>
                          <span className="text-white font-medium">{challenge?.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-1.5">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-1.5 rounded-full transition-all duration-300 ease-smooth"
                            style={{ width: `${challenge?.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            ))}
          </div>
        ) : (
          // [NEW] DailyChallenge empty state
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 text-center text-gray-400">
            <div className="mx-auto grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-white/10 text-white/70">
              <Icon name="Target" size={18} />
            </div>
            <p className="mt-3 text-sm">No challenges today. Check back tomorrow!</p>
          </div>
        )}
      </div>

      {/* AI Tips */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">AI Coach Tips</h3>

        {aiTips?.length > 0 ? (
          <div className="space-y-3">
            {aiTips.map((tipItem) => (
              <SpotlightCard
                key={tipItem?.id}
                className="custom-spotlight-card relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
                spotlightColor={tipItem?.spotlight}
              >
                <div className="relative z-10 p-4 rounded-2xl hover:bg-white/5 transition-all duration-200 ease-smooth">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400">
                      <Icon name={tipItem?.icon || 'Lightbulb'} size={16} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-purple-400">{tipItem?.category}</span>
                      </div>
                      <p className="text-sm text-white">{tipItem?.tip}</p>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <div className="w-full flex justify-center">
              <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl flex items-center justify-center">
                <Icon name="Lightbulb" size={32} className="text-white/55" />
              </div>
            </div>
            <p className="mt-3 text-sm">No tips available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingSessions;
